import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

//hooks
import useMedias from '@/utils/hooks/swr/useMedias';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import PropTypes from 'prop-types';

import { ALL, MENTION_ICON } from '@/lib/manage/mentions';
//lib
import { CUSTOM } from '@/lib/periods';
import { mentionsSortTypes } from '@/lib/posts/postSortTypes';
import { EXPORT_ENDPOINT_TYPES, MENTION, POST } from '@/lib/posts/postTypes';

import Carousel from '@/components/base/Carousel';
import { PostComponent } from '@/components/base/Post/PostComponent';
import Select from '@/components/base/Select';
//components
import { WidgetMedia } from '@/components/base/WidgetMedia';

// routes
import { GET_MENTIONS_FEED } from '@/routes/apiEndpoints';
import { LISTENING_FEED } from '@/routes/privateRoutes';
const CustomDashboardWidgetOptionSaver = dynamic(
    () => import('@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetOptionSaver')
);

//pages composite
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';

import Loader from '@/components/base/Loader';
import ButtonDownloadDirectExport from '@/components/composite/Button/ButtonDownloadDirectExport';
import Instagram from '@/components/icons/Instagram';
import CustomDashboardWidgetActions from '@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetActions';

import MentionMediaViewerModal from './MentionMediaViewerModal';

const SelectMentionType = ({ data, setRequestParams, onChange, currentType }) => {
    const [activeType, setActiveType] = useState(
        currentType ? data.find((option) => option.value === currentType) ?? data[0] : data[0]
    );

    useEffect(() => {
        const value = activeType?.value !== 'all' ? activeType?.value : '';
        setRequestParams((prevState) => ({
            ...prevState,
            filter: value
        }));
        if (typeof onChange === 'function') {
            onChange(value);
        }
    }, [activeType?.value, setRequestParams]);
    return (
        <Select
            selected={
                data.filter((element) => element?.value === activeType?.value)?.[0] ?? data?.[0]
            }
            setSelected={setActiveType}
            options={data}
            noBorder
            buttonBaseStyle="noBorder"
        />
    );
};

SelectMentionType.propTypes = {
    data: PropTypes.array,
    setRequestParams: PropTypes.func,
    onChange: PropTypes.func,
    currentType: PropTypes.string
};

export const MentionMediasWidget = ({
    config,
    periodData,
    numberFormat,
    endpoint = GET_MENTIONS_FEED,
    onDelete,
    isDashboard = false
}) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [socialProfileSelected, setSocialProfileSelected] = useState(
        isDashboard
            ? activatedSocialProfiles.find((sp) => sp.id === config.social_profiles[0])
            : activeSocialProfile
    );
    const network = socialProfileSelected.social_network_type;
    const { t } = useTranslation('commons');
    const [isLoadingExportXlsDashboard, setIsLoadingExportXlsDashboard] = useState(false);
    const [mediaViewerParams, setMediaViewerParams] = useState({ isOpen: false, mediaId: null });

    const {
        dataMedias,
        requestParams,
        mediasError,
        // Loaders
        isLoadingInitialData,
        // Functions
        mutateMedias,
        setRequestParams
    } = useMedias({
        activeSPID: socialProfileSelected?.id,
        limit: 10,
        endpoint
    });

    useEffect(() => {
        if (periodData.period === CUSTOM) {
            setRequestParams((prevState) => ({
                ...prevState,
                period: periodData.period,
                start: periodData.start,
                end: periodData.end,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        } else {
            setRequestParams((prevState) => ({
                ...prevState,
                period: periodData.period,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        }
    }, [
        periodData.period,
        periodData.start,
        periodData.end,
        periodData.compareTo,
        setRequestParams
    ]);

    // relaunch request if active SocialProfile changes
    useEffect(() => {
        if (!isDashboard) {
            setSocialProfileSelected(activeSocialProfile);
        }
    }, [activeSocialProfile?.id]);

    const canExportMedias =
        !isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length > 0;

    const onClickDashboardXlsExport = async (callback) => {
        setIsLoadingExportXlsDashboard(true);
        await callback();
        setIsLoadingExportXlsDashboard(false);
    };

    return (
        <WidgetMedia>
            <WidgetMedia.Header>
                <div className="flex flex-1 items-center space-x-3">
                    {isDashboard ? (
                        <div>
                            <CustomDashboardWidgetActions.SelectSP
                                config={config}
                                socialProfiles={config.social_profiles}
                                setSocialProfileSelected={setSocialProfileSelected}
                                isMedia
                            />
                        </div>
                    ) : (
                        <Instagram />
                    )}

                    <h3>{t('analytics:chart.instagram.widget_last_mentioned_posts')}</h3>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="w-44">
                        {isDashboard ? (
                            <CustomDashboardWidgetOptionSaver>
                                {(saveWidgetOption) => (
                                    <SelectMentionType
                                        setRequestParams={setRequestParams}
                                        data={mentionsSortTypes(t)}
                                        onChange={(filter) => {
                                            saveWidgetOption(config, 'filter', filter, '');
                                        }}
                                        currentType={config?.parameters?.filter}
                                    />
                                )}
                            </CustomDashboardWidgetOptionSaver>
                        ) : (
                            <SelectMentionType
                                setRequestParams={setRequestParams}
                                data={mentionsSortTypes(t)}
                            />
                        )}
                    </div>
                    {isDashboard ? (
                        <CustomDashboardWidgetActions.Popover className="bg-white dark:bg-icoDarkMode-deepBlack">
                            {canExportMedias ? (
                                <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                                    <ButtonDownloadDirectExport
                                        isLoading={isLoadingExportXlsDashboard}
                                        requestParams={{
                                            ...requestParams,
                                            filter_post_type:
                                                requestParams?.filter !== ''
                                                    ? [requestParams.filter]
                                                    : []
                                        }}
                                        typeExport={EXPORT_ENDPOINT_TYPES[MENTION]}
                                        currentSocialProfile={socialProfileSelected}>
                                        {({ generateReport, isLoading }) => (
                                            <button
                                                disabled={isLoading}
                                                className="flex w-full items-center p-2 disabled:text-icoGray-500 disabled:dark:text-icoDarkMode-wolf"
                                                onClick={() =>
                                                    onClickDashboardXlsExport(generateReport)
                                                }>
                                                {isLoading ? (
                                                    <Loader className="mr-1 h-3 w-3" />
                                                ) : null}
                                                {t('analytics:xls_export')}
                                            </button>
                                        )}
                                    </ButtonDownloadDirectExport>
                                </li>
                            ) : null}
                            <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                                <button className="w-full p-2 text-left" onClick={onDelete}>
                                    {t('dashboard:remove_widget')}
                                </button>
                            </li>
                        </CustomDashboardWidgetActions.Popover>
                    ) : canExportMedias ? (
                        <ButtonDownloadDirectExport
                            requestParams={{
                                ...requestParams,
                                filter_post_type:
                                    requestParams?.filter !== '' ? [requestParams.filter] : []
                            }}
                            typeExport={EXPORT_ENDPOINT_TYPES[MENTION]}
                            currentSocialProfile={socialProfileSelected}>
                            {({ generateReport, isLoading }) => (
                                <WidgetMedia.ExportButton
                                    onClick={generateReport}
                                    isLoading={isLoading}
                                    tooltipContent={t('analytics:xls_export')}
                                />
                            )}
                        </ButtonDownloadDirectExport>
                    ) : null}
                </div>
            </WidgetMedia.Header>

            {isLoadingInitialData && !dataMedias ? (
                <WidgetMedia.Placeholder
                    placeholderConfig={{ network: network }}
                    wrapperClass="xl:space-x-m 2xl:space-x-5"
                    postClass="min-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)]"
                    postFormat="square"
                />
            ) : null}
            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length === 0 ? (
                <WidgetMedia.NoData
                    networks={[network]}
                    message={t('analytics:no_posts_in_period')}
                />
            ) : null}

            {mediasError && !isLoadingInitialData ? (
                <WidgetMedia.ErrorFallBack resetErrorBoundary={() => mutateMedias()} />
            ) : null}

            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length > 0 ? (
                <ErrorBoundary
                    FallbackComponent={WidgetMedia.ErrorFallBack}
                    onReset={() => mutateMedias()}>
                    <WidgetMedia.Carousel
                        navPosition="external"
                        className="xl:space-x-m 2xl:space-x-5">
                        {dataMedias?.map((medias) => {
                            return medias?.['medias']?.map((media, index) => {
                                return (
                                    <Carousel.Slide
                                        key={media?.id}
                                        arial-label={`media ${media?.id}`}
                                        className="min-w-[calc(33.3%-6px)] max-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] xl:max-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)] 2xl:max-w-[calc(20%-16px)]">
                                        <PostComponent
                                            data-testid={`post-${index}`}
                                            network={network}
                                            type="medias"
                                            isLoading={false}
                                            data={{
                                                ...media,
                                                type: MENTION_ICON[
                                                    media.mentioned_in.length > 1
                                                        ? ALL
                                                        : media.mentioned_in[0]
                                                ]
                                            }}
                                            config={{
                                                postType: POST,
                                                postFormat: 'square',
                                                activeFilter: requestParams?.sort?.column ?? null,
                                                network,
                                                numberFormat: numberFormat
                                            }}
                                            openViewer={(e) => {
                                                setMediaViewerParams({
                                                    isOpen: true,
                                                    mediaId: media?.id,
                                                    mediaType: media?.media_type
                                                });
                                                e.stopPropagation();
                                            }}
                                            postConfig={{
                                                hasFooter: false,
                                                showAlbums: false,
                                                showLabels: false
                                            }}
                                            remoteMedia
                                        />
                                    </Carousel.Slide>
                                );
                            });
                        })}
                        <Carousel.Slide
                            arial-label="show-more"
                            className="group min-w-[calc(33.3%-6px)] max-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] xl:max-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)] 2xl:max-w-[calc(20%-16px)]">
                            <WidgetMedia.ShowMore
                                url={`${LISTENING_FEED.replace('[id]', 'tags-mentions')}${
                                    isDashboard
                                        ? `?socialProfileId=${socialProfileSelected.id}`
                                        : ''
                                }`}
                                textContent={t('show_more_posts')}
                            />
                        </Carousel.Slide>
                    </WidgetMedia.Carousel>
                </ErrorBoundary>
            ) : null}
            <MentionMediaViewerModal
                isOpen={mediaViewerParams?.isOpen}
                toggle={() => {
                    setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                }}
                mediaId={mediaViewerParams?.mediaId}
                mutateMedias={mutateMedias}
                mediaRequestParams={requestParams}
            />
        </WidgetMedia>
    );
};

MentionMediasWidget.propTypes = {
    sortFields: PropTypes.arrayOf(PropTypes.string),
    page: PropTypes.string,
    periodData: PropTypes.object,
    numberFormat: PropTypes.string,
    network: PropTypes.string
};
